//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import Application from "eai-configurator-ui/components/Application";
import ConfigurationDashboard from "eai-configurator-ui/components/configuration/ConfigurationDashboard";
import AccountButton from "eai-configurator-ui/components/header/buttons/AccountButton";
import HelpButton from "eai-configurator-ui/components/header/buttons/HelpButton";
import MessagesButton from "eai-configurator-ui/components/header/buttons/MessagesButton";
import FindInPageIcon from "@material-ui/icons/FindInPage";
import DonutLargeIcon from "@material-ui/icons/DonutLarge";
import PeopleIcon from "@material-ui/icons/People";
import ConfigurationProfileButton from "eai-configurator-ui/components/configuration/buttons/ConfigurationProfileButton";
import SaveConfigurationButton from "eai-configurator-ui/components/configuration/buttons/SaveConfigurationButton";
import {HashRouter as Router} from "react-router-dom";
import AuditorWelcomeOverview from "src/components/auditor/pages/AuditorWelcomeOverview";
import AuditorEventTablePage from "src/components/auditor/pages/event/AuditorEventTablePage";
import AuditorUsageTablePage from "src/components/auditor/pages/usage/AuditorUsageTablePage";
import AuditorUserActivityTablePage from "src/components/auditor/pages/user_activity/AuditorUserActivityTablePage";
import Login from "eai-configurator-ui/components//login/Login";
import ManageUsersButton from "eai-configurator-ui/components/header/buttons/ManageUsersButton";
import ManageUsersDialog from "eai-configurator-ui/components/configuration/dialog/ManageUsersDialog";
import {useConfigProfile} from "eai-configurator-ui/components/configuration/profile/ConfigurationProfile";
import * as Utils from "eai-configurator-ui/components/Utils";
import * as wjcCore from "@grapecity/wijmo";
import {useSessionInfo} from "eai-configurator-ui/components/SessionInfo";
import TeamsConfigurationPermission from "eai-configurator-ui/components/configuration/permission/TeamsConfigurationPermission";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import AuditorTasksPage from "src/components/auditor/pages/AuditorTasksPage";

function AuditorApplication() {
  const [manageUsersDialogOpen, setManageUsersDialogOpen] = React.useState(
    false
  );
  const [profile] = useConfigProfile();
  const [sessionInfo] = useSessionInfo();
  const [appSettings, setAppSettings] = React.useState({
    primaveraDataServicesSource: false,
    jdbcSource: false,
    userCategories: [],
    eventTabs: []
  });
  const [usageTabs, setUsageTabs] = React.useState([]);
  const [userTabs, setUserTabs] = React.useState([]);

  const [navigationIndex, setNavigationIndex] = React.useState(0);
  const [subNavigationIndex, setSubNavigationIndex] = React.useState(null);

  //
  // Process the event tabs
  const [eventNavigation, setEventNavigation] = React.useState([]);

  React.useEffect(() => {
    if (appSettings && Array.isArray(appSettings.eventTabs) && Array.isArray(appSettings.eventGroups)) {
      //
      // Builds the event navigation options
      //
      const eventTabMap = new Map();

      // Create all groups
      appSettings.eventGroups.forEach((groupName) => {
        eventTabMap.set(groupName, []);
      })

      // Maps all entities to those groups
      appSettings.eventTabs.forEach((eventTab) => {
        const groupName = eventTab.groupName;
        const collection = eventTabMap.get(groupName);
        const component = {
          label: eventTab.displayName,
          component: (
            <AuditorEventTablePage
              path="auditor:event"
              appSettings={appSettings}
              navigateToResults={navigateToResults}
              selectedEntity={{
                key: eventTab.tabName,
                name: eventTab.tabName,
                displayName: eventTab.displayName,
                supportsUndoOrUndeletion: eventTab.supportsUndoOrUndeletion
              }}/>),
          subNavigationIndex: eventTab.tabIndex
        }
        if (collection == null) {
          eventTabMap.set(groupName, [component]);
        } else {
          collection.push(component);
        }
      });

      // Builds the navigation
      const navigation = [];
      eventTabMap.forEach((value, key) => {
        navigation.push({
          key: key,
          section: "Reporting",
          label: key,
          icon: <FindInPageIcon/>,
          subNavigation: [...value]
        });
      });
      setEventNavigation([...navigation]);
    }

    if (appSettings && Array.isArray(appSettings.usageTabs)) {
      //
      // Builds the usage  navigation options
      //
      const usageTabCollection = [];
      appSettings.usageTabs.forEach((eventTab) => {
        const component = {
          label: eventTab.displayName,
          component: (
            <AuditorUsageTablePage path="auditor:usage" appSettings={appSettings}
                                   selectedEntity={{key: eventTab.tabName, name: eventTab.tabName, ...eventTab}}/>),
          subNavigationIndex: eventTab.tabIndex
        }
        usageTabCollection.push(component);
      });
      setUsageTabs(usageTabCollection);
    }

    if (appSettings && Array.isArray(appSettings.userTabs)) {
      //
      // Builds the user  navigation options
      //
      const userTabCollection = [];
      appSettings.userTabs.forEach((eventTab) => {
        const component = {
          label: eventTab.displayName,
          component: (
            <AuditorUserActivityTablePage path="auditor:userActivity" appSettings={appSettings}
                                          selectedEntity={{key: eventTab.tabName, name: eventTab.tabName, displayName: eventTab.displayName}}/>),
          subNavigationIndex: eventTab.tabIndex
        }
        userTabCollection.push(component);
      });
      setUserTabs(userTabCollection);
    }
  }, [appSettings])

  React.useEffect(() => {
    if (sessionInfo) {
      Utils.fetchData("auditor/wijmo/license", null, false, false)
        .then((response) => response.text())
        .then((data) => {
          wjcCore.setLicenseKey(data);
        })
        .catch(function (error) {
          // Do not inform the user but print some traces in the console log
          console.log("Wijmo license fetch failed");
          console.log(error);
        });
    }
  }, [sessionInfo]);

  React.useEffect(() => {
    if (sessionInfo) {
      Utils.fetchData("auditor/appSettings", null, false, true)
        .then((response) => {
          console.table(response);
          setAppSettings(response);
        }).catch((response) => {
        // Need to show error
        Utils.getErrorMessage(response, "Retrieve settings failed.")
          .then(alert);
      });
    }
  }, [sessionInfo]);

  const handleNavigationChange = (index, subIndex) => {
    setSubNavigationIndex(subIndex);
    setNavigationIndex(index);
    console.log(">> handleNavigationChange(" + index + ", " + subIndex + ")");
  };

  const navigateToResults = () => {
    console.log("navigateToResults")
    // setSubNavigationIndex(null);
    // setNavigationIndex(0);
  };

  return (
    <Router>
      <Application
        login={<Login useDatabase appName={"Auditor"}/>}
        application={{
          routes: [
            {
              path: "/auditor",
              component: (
                <ConfigurationDashboard
                  navigationPanelWidth={240}
                  navigationIndex={navigationIndex}
                  subNavigationIndex={subNavigationIndex}
                  handleNavigationChange={handleNavigationChange}
                  navigation={[
                    {
                      section: "Undo/ Undelete",
                      label: "Tasks",
                      icon: <AssignmentTurnedInIcon/>,
                      component: <AuditorTasksPage/>,
                    },
                    ...eventNavigation,
                    appSettings.jdbcSource && {
                      key: "Reporting-Usage",
                      section: "Reporting",
                      label: "Usage Reports",
                      icon: <DonutLargeIcon/>,
                      subNavigation: [...usageTabs]
                    },
                    appSettings.jdbcSource && {
                      section: "Reporting",
                      label: "User Reports",
                      icon: <PeopleIcon/>,
                      subNavigation: [...userTabs]
                    },
                  ].filter(function (el) {
                    return el != null && el !== false;
                  })}
                  welcomeContent={{
                    label: "Welcome",
                    component: <AuditorWelcomeOverview/>,
                    configuration: "auditor",
                  }}
                  headerButtons={[
                    <ConfigurationProfileButton profile={profile}/>,
                    <MessagesButton/>,
                    <HelpButton/>,
                    <AccountButton
                      buttons={[
                        <ManageUsersButton
                          handleClick={() => setManageUsersDialogOpen(true)}
                        />,
                      ]}
                    />,
                  ]}
                  actionButtons={[
                    <SaveConfigurationButton
                      key="manageDefinitionsButton"
                      label="Layouts"
                      profileLabel={"Layout"}
                      doNotWarnUserOfPendingEdits
                      PermissionConfigurationComponent={
                        TeamsConfigurationPermission
                      }
                    />,
                  ]}
                />
              ),
            },
          ],
          defaultRoute: "auditor",
        }}
      >
        <ManageUsersDialog
          open={manageUsersDialogOpen}
          allowConfiguringTeams
          handleClose={() => setManageUsersDialogOpen(false)}
        />
      </Application>
    </Router>
  );
}

export default AuditorApplication;
